import { z } from 'zod';
import { uuidSchema } from './common.js';

/**
 * Virtual comp DTOs (SIL-1825, phase 1).
 *
 * A virtual comp is a DIRECTIONAL sum-of-components price for a SKU with no
 * real comp. OPS-ONLY surface: component lines carry supplier prices, part
 * numbers and URLs, none of which may reach a client role (the client
 * receives only baselinePriceSourceId = 'virtual_comp' and its label, via
 * the existing snapshot grants).
 *
 * Request shape is strict on purpose: the price is NEVER accepted from the
 * caller (computed server-side as sum(quantity * unitPriceUsd)), the actor is
 * server-derived, and source URLs are validated here but NEVER fetched
 * server-side nor fed to sku_scrape_target / POST /api/skus/:skuId/scrape.
 */

export const VIRTUAL_COMP_ALGORITHM_VERSION = 'sum_of_lines_v1';
export const VIRTUAL_COMP_MAX_LINES = 50;
/** Per-line caps keep the sum inside numeric(12,2) with room to spare. */
export const VIRTUAL_COMP_MAX_UNIT_PRICE_USD = 500_000;
export const VIRTUAL_COMP_MAX_QUANTITY = 1_000;
export const VIRTUAL_COMP_MAX_TOTAL_USD = 100_000_000;

/** Part numbers: alphanumerics plus . _ - / + only (no spaces, quotes, control chars). */
const partNumberSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._\-/+]*$/, 'part may only contain letters, digits and . _ - / +');

const descriptionSchema = z.string().trim().min(1).max(500);

const quantitySchema = z.number().int().positive().max(VIRTUAL_COMP_MAX_QUANTITY);

/** At most two decimals: the stored price is numeric(12,2) and must equal the sum of its lines. */
const unitPriceSchema = z
  .number()
  .finite()
  .positive()
  .max(VIRTUAL_COMP_MAX_UNIT_PRICE_USD)
  .refine((n) => Math.abs(Math.round(n * 100) - n * 100) < 1e-6, 'unitPriceUsd may have at most 2 decimals');

/**
 * https only, parseable, no embedded credentials, <= 2048 chars. Used on input
 * AND re-checked at render (`safeHttpsUrl`) - a stored value is never trusted.
 */
export function safeHttpsUrl(raw: string): string | null {
  if (raw.length > 2048) return null;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:') return null;
  if (u.username !== '' || u.password !== '') return null;
  return u.toString();
}

const httpsUrlSchema = z
  .string()
  .max(2048)
  .refine((v) => safeHttpsUrl(v) !== null, 'sourceUrl must be a valid https:// URL without credentials');

const webLineSchema = z
  .object({
    sourceKind: z.literal('web'),
    part: partNumberSchema,
    description: descriptionSchema,
    quantity: quantitySchema,
    unitPriceUsd: unitPriceSchema,
    sourceUrl: httpsUrlSchema,
    observedAt: z.string().datetime(),
  })
  .strict();

/**
 * A menu_snapshot line names a part number inside one stored configurator
 * snapshot; the server resolves the unit price from that snapshot's
 * delta_matrix_json (never a live configurator call) - the caller supplies no price.
 */
const menuSnapshotLineSchema = z
  .object({
    sourceKind: z.literal('menu_snapshot'),
    menuSnapshotId: uuidSchema,
    part: partNumberSchema,
    description: descriptionSchema.optional(),
    quantity: quantitySchema,
  })
  .strict();

export const virtualCompLineInputSchema = z.discriminatedUnion('sourceKind', [webLineSchema, menuSnapshotLineSchema]);
export type VirtualCompLineInput = z.infer<typeof virtualCompLineInputSchema>;

export const createVirtualCompBodySchema = z
  .object({
    skuId: uuidSchema,
    lines: z.array(virtualCompLineInputSchema).min(1).max(VIRTUAL_COMP_MAX_LINES),
  })
  .strict();
export type CreateVirtualCompBody = z.infer<typeof createVirtualCompBodySchema>;

export const virtualCompIdParamSchema = z.object({ id: uuidSchema });

export const listVirtualCompsQuerySchema = z.object({
  skuId: uuidSchema.optional(),
  includeSuperseded: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

// ---------------------------------------------------------------------------
// Response (ops-only). Built field-by-field in the route; never a row spread.
// ---------------------------------------------------------------------------

export const virtualCompLineSchema = z.object({
  part: z.string(),
  description: z.string(),
  quantity: z.number(),
  unitPriceUsd: z.number(),
  sourceKind: z.enum(['menu_snapshot', 'web']),
  menuSnapshotId: z.string().nullable(),
  /** https-only; null when the stored value fails the render-time check. */
  sourceUrl: z.string().nullable(),
  observedAt: z.string(),
});
export type VirtualCompLine = z.infer<typeof virtualCompLineSchema>;

export const virtualCompSchema = z.object({
  id: z.string().uuid(),
  skuId: z.string().uuid(),
  priceUsd: z.number(),
  componentLines: z.array(virtualCompLineSchema),
  algorithmVersion: z.string(),
  computedAt: z.string(),
  createdByActorSub: z.string(),
  supersededAt: z.string().nullable(),
  supersededReason: z.enum(['replaced', 'real_comp_arrived', 'retracted']).nullable(),
});
export type VirtualComp = z.infer<typeof virtualCompSchema>;

export const virtualCompListResponseSchema = z.object({ items: z.array(virtualCompSchema) });
export const createVirtualCompResponseSchema = z.object({
  virtualComp: virtualCompSchema,
  /** false when an identical live virtual comp already existed (idempotent replay). */
  created: z.boolean(),
});

/** Money in integer cents: avoids float drift between the API sum and the DB trigger. */
export function toCents(usd: number): number {
  return Math.round(usd * 100);
}

/** price_usd = sum(quantity * unit_price) computed in cents; the DB trigger re-asserts it. */
export function sumLinesUsd(lines: ReadonlyArray<{ quantity: number; unitPriceUsd: number }>): number {
  let cents = 0;
  for (const l of lines) cents += l.quantity * toCents(l.unitPriceUsd);
  return cents / 100;
}
